import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatUtcDateTimeToTimeZone } from '@belong/common';
import { Text } from '@belong/ui';
import classNames from 'classnames/bind';
import Checkbox from 'components/Checkbox/Checkbox';
import { fetchSmartLockInstallAvailabilityWindow } from 'store/redux/maintenance/actions';
import styles from './ImprovementModal.module.css';
import { SmartLockAvailabilityPicker } from './smart-lock-availability-picker';
import {
  SMART_LOCK_REQUIRED_SLOTS,
  SMART_LOCK_TIME_OPTIONS,
  type RawAvailability,
  type SmartLockAvailabilityWindow,
  type SmartLockInstallAvailabilityValue,
  buildSmartLockAvailabilityPayload,
  getDisplaySlots,
  formatSlotLabel,
  getInitialSmartLockValue,
  getSmartLockCardSlotLabels,
  getDatesInWindow,
  isSmartLockAvailabilityDeferred,
  isSmartLockAvailabilityResolved,
  normalizeSmartLockAvailability,
  shouldSaveSmartLockAvailability,
} from './smart-lock-install-availability.utils';

const cx = classNames.bind(styles);

type SmartLockWindowStatus = 'idle' | 'loading' | 'ready' | 'error';

const getEmptyValue = (
  maintenanceUniqueId: string,
  savedAvailability: RawAvailability[] | null | undefined,
): SmartLockInstallAvailabilityValue => ({
  selections: [],
  isDeferred:
    !normalizeSmartLockAvailability(savedAvailability).length &&
    isSmartLockAvailabilityDeferred(maintenanceUniqueId),
});

type UseSmartLockInstallAvailabilityArgs = {
  maintenanceUniqueId: string;
  savedAvailability: RawAvailability[] | null | undefined;
  /** Fetch the selectable window only while the editable section is shown. */
  enabled: boolean;
};

export function useSmartLockInstallAvailability({
  maintenanceUniqueId,
  savedAvailability,
  enabled,
}: UseSmartLockInstallAvailabilityArgs) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState<SmartLockWindowStatus>('idle');
  const [dates, setDates] = useState<string[]>([]);
  const [value, setValue] = useState<SmartLockInstallAvailabilityValue>(() =>
    getEmptyValue(maintenanceUniqueId, savedAvailability),
  );
  const wasEnabledRef = useRef(false);

  // The saved value only seeds the picker once the window arrives.
  const savedRef = useRef(savedAvailability);
  savedRef.current = savedAvailability;

  useEffect(() => {
    if (!enabled || !maintenanceUniqueId) {
      // Closing the modal drops an unsaved pick, so reopening starts from what is saved.
      if (wasEnabledRef.current) {
        setStatus('idle');
        setDates([]);
        setValue(getEmptyValue(maintenanceUniqueId, savedRef.current));
      }
      wasEnabledRef.current = false;
      return undefined;
    }
    wasEnabledRef.current = true;

    let isCancelled = false;
    setStatus('loading');

    (async () => {
      try {
        const availabilityWindow = (await dispatch(
          fetchSmartLockInstallAvailabilityWindow(maintenanceUniqueId),
        )) as unknown as SmartLockAvailabilityWindow;
        if (isCancelled) return;

        const nextDates = getDatesInWindow(availabilityWindow);
        setDates(nextDates);
        setValue((prev) => ({
          ...getInitialSmartLockValue(savedRef.current, nextDates),
          isDeferred: prev.isDeferred,
        }));
        setStatus('ready');
      } catch {
        if (isCancelled) return;
        // Keep what is saved so an unchanged, complete pick still counts.
        setValue((prev) => ({
          ...getInitialSmartLockValue(savedRef.current),
          isDeferred: prev.isDeferred,
        }));
        setStatus('error');
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, enabled, maintenanceUniqueId]);

  return {
    status,
    dates,
    value,
    setValue,
    isResolved: isSmartLockAvailabilityResolved(value),
    hasChanges: shouldSaveSmartLockAvailability(value, savedAvailability),
    payload: buildSmartLockAvailabilityPayload(value),
  };
}

function CalendarIcon({ size = 46 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 46 46"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ gridArea: 'icon', flexShrink: 0 }}
    >
      <rect
        x="5"
        y="8"
        width="36"
        height="32"
        rx="3"
        stroke="#325E77"
        strokeWidth="2"
      />
      <path d="M5 17h36M14 4v8M32 4v8" stroke="#325E77" strokeWidth="2" />
      <path
        d="M12 24h4M21 24h4M30 24h4M12 31h4M21 31h4"
        stroke="#3EE4A9"
        strokeWidth="2.5"
      />
    </svg>
  );
}

type SmartLockInstallAvailabilityProps = {
  /** Only used to label the times with the home's time zone. */
  timeZone?: string;
  status: SmartLockWindowStatus;
  dates: string[];
  value: SmartLockInstallAvailabilityValue;
  onChange: (value: SmartLockInstallAvailabilityValue) => void;
};

export function SmartLockInstallAvailability({
  timeZone,
  status,
  dates,
  value,
  onChange,
}: SmartLockInstallAvailabilityProps) {
  const isLoading = status === 'idle' || status === 'loading';
  const isPickerAvailable = status === 'ready' && dates.length > 0;

  const timeZoneLabel = timeZone
    ? ` (${formatUtcDateTimeToTimeZone({
        dateTime: new Date(),
        format: 'z',
        timeZone,
      })})`
    : '';

  return (
    <div aria-busy={isLoading}>
      <div className={cx('information-grid')}>
        <CalendarIcon />
        <Text className={cx('title')} fontWeight="semibold">
          Installation Availability
        </Text>
        <Text variant="p1" className={cx('detail')}>
          {`Choose ${SMART_LOCK_REQUIRED_SLOTS} times when our installer can come by. We will confirm one of them and remind you before the visit.`}
        </Text>
      </div>

      {!value.isDeferred && (
        <div className="mt-xl">
          {isLoading && (
            <Text className="text-center text-dark-gray">
              Loading available times...
            </Text>
          )}
          {!isLoading && !isPickerAvailable && (
            <Text className="text-dark-gray">
              We could not load the available installation times right now.
              Please try again later, or let us know you don&apos;t know your
              availability yet.
            </Text>
          )}
          {isPickerAvailable && (
            <>
              <SmartLockAvailabilityPicker
                dates={dates}
                timeFrames={SMART_LOCK_TIME_OPTIONS}
                requiredSlots={SMART_LOCK_REQUIRED_SLOTS}
                value={value.selections}
                onChange={(selections) => onChange({ ...value, selections })}
              />
              <Text variant="p1" className="mt-xs text-right text-dark-gray">
                {`Times are in the home's local time${timeZoneLabel}`}
              </Text>
            </>
          )}
        </div>
      )}

      <div className={cx('checkbox-container', 'mt-sm', '!mb-xl')}>
        <Checkbox
          alignCenter
          alignWithField={false}
          useOnChangeFormToHandle
          disabled={isLoading}
          value={value.isDeferred}
          label="I DON'T KNOW MY AVAILABILITY YET"
          onSelect={(isDeferred: boolean) => onChange({ ...value, isDeferred })}
        />
      </div>
    </div>
  );
}

type SmartLockInstallAvailabilitySummaryProps = {
  savedAvailability: RawAvailability[] | null | undefined;
};

/** Read-only view for an install that can no longer be rescheduled. */
export function SmartLockInstallAvailabilitySummary({
  savedAvailability,
}: SmartLockInstallAvailabilitySummaryProps) {
  const slots = getDisplaySlots(
    normalizeSmartLockAvailability(savedAvailability),
  );

  return (
    <div>
      <div className={cx('information-grid')}>
        <CalendarIcon />
        <Text className={cx('title')} fontWeight="semibold">
          Installation Availability
        </Text>
        <div className={cx('detail')}>
          {slots.length ? (
            slots.map((slot) => (
              <Text
                key={`${slot.date}-${slot.time}`}
                variant="p1"
                fontWeight="semibold"
              >
                {formatSlotLabel(slot)}
              </Text>
            ))
          ) : (
            <Text variant="p1" className="text-dark-gray">
              Not provided yet
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}

export function SmartLockAvailabilityPendingBanner() {
  return (
    <div className="flex w-full items-center gap-xs bg-green-light-bg px-sm py-xs text-left">
      <CalendarIcon size={32} />
      <div className="flex flex-col">
        <Text fontWeight="semibold">Choose Install Times</Text>
        <Text variant="p1">So we can book your visit</Text>
      </div>
    </div>
  );
}

type SmartLockAvailabilityCardSlotsProps = {
  savedAvailability: RawAvailability[] | null | undefined;
};

/** The saved slots over the item's card image in the report; nothing until they're saved. */
export function SmartLockAvailabilityCardSlots({
  savedAvailability,
}: SmartLockAvailabilityCardSlotsProps) {
  const labels = getSmartLockCardSlotLabels(savedAvailability);
  if (labels.length === 0) return null;

  return (
    <div className="flex flex-col items-end">
      {labels.map((label) => (
        <p
          key={label}
          className="text-right text-[1.2rem] font-semibold leading-p1 text-white"
        >
          {label}
        </p>
      ))}
    </div>
  );
}
